'use client'

import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { UserControl } from '@/auth/AuthProvider'
import Sidebar, { isNavigationItemActive } from './Sidebar'
import { AnalyticsIcon, FishingIcon, InventoryIcon, MissionControlIcon, OperationsIcon, SettingsIcon, StorefrontsIcon } from './icons'
import DiamondMark from './brand/DiamondMark'
import IosInstallPrompt from './IosInstallPrompt'
import { useMobileNavigation } from './MobileNavigationContext'

const items = [
  { label: 'Диспетчерская', href: '/', icon: <MissionControlIcon /> },
  { label: 'Склад', href: '/inventory', icon: <InventoryIcon /> },
  { label: 'Аналитика и покупатели', href: '/analytics', icon: <AnalyticsIcon /> },
  { label: 'Рыбалка продаж', href: '/fishing', icon: <FishingIcon /> },
  { label: 'Выставка золота', href: '/gold-exhibition', icon: <StorefrontsIcon /> },
  { label: 'Операции', href: '/operations', icon: <OperationsIcon /> },
  { label: 'Настройки', href: '/settings', icon: <SettingsIcon /> },
]

function pageTitle(pathname: string): string {
  if (pathname.startsWith('/customers/')) return 'Покупатель'
  if (pathname.startsWith('/analytics')) return 'Аналитика'
  if (pathname.startsWith('/fishing')) return 'Рыбалка продаж'
  if (pathname.startsWith('/gold-exhibition')) return 'Выставка золота'
  if (pathname.startsWith('/settings')) return 'Настройки'
  if (pathname.startsWith('/operations')) return 'Операции'
  if (pathname.startsWith('/orders')) return 'Склад'
  if (pathname === '/inventory/recovery') return 'Восстановление привязки'
  if (pathname.startsWith('/inventory/')) return 'Изделие'
  if (pathname.startsWith('/inventory')) return 'Склад'
  return 'Диспетчерская'
}

export default function AppNavigation() {
  const [open, setOpen] = useState(false)
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { navigation } = useMobileNavigation()
  const fallbackCanGoBack = pathname.startsWith('/customers/') || (pathname.startsWith('/inventory/') && pathname !== '/inventory')
  const canGoBack = navigation !== null ? navigation.backTo !== null : fallbackCanGoBack
  const title = navigation?.title ?? pageTitle(pathname)
  const action = navigation?.action

  const handleLeadingAction = () => {
    if (!canGoBack) {
      setOpen(true)
      return
    }
    if (navigation?.onBack !== undefined) {
      navigation.onBack()
      return
    }
    if (navigation?.backTo !== null && navigation?.backTo !== undefined) navigate(navigation.backTo)
    else navigate(-1)
  }

  useEffect(() => setOpen(false), [pathname])

  return (
    <>
      <Sidebar />
      <header className="md:hidden fixed inset-x-0 top-0 z-50 h-[calc(52px+env(safe-area-inset-top))] pt-[env(safe-area-inset-top)] bg-paper/95 backdrop-blur border-b border-rule flex items-center px-3">
        <button
          onClick={handleLeadingAction}
          className="size-11 -ml-1 inline-flex items-center justify-center text-ink"
          aria-label={canGoBack ? 'Назад' : 'Открыть меню'}
        >
          {canGoBack ? (
            <svg aria-hidden="true" viewBox="0 0 24 24" className="size-8" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
          )}
        </button>
        <div className="pointer-events-none absolute left-1/2 max-w-[60vw] -translate-x-1/2 text-center">
          <div className="truncate text-[15px] font-semibold leading-5">{title}</div>
          {navigation?.subtitle && <div className="truncate text-[11px] font-medium leading-3 text-ink-muted">{navigation.subtitle}</div>}
        </div>
        <div className="ml-auto flex size-11 items-center justify-center">
          {action ? <button type="button" aria-label={action.ariaLabel} title={action.ariaLabel} onClick={() => navigate(action.to)} className="grid size-11 place-items-center rounded-full text-ink-muted active:bg-bone">
            {action.kind === 'settings' ? <SettingsIcon /> : <svg aria-hidden="true" viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7.5h16v12H4z" /><path d="M3 4.5h18v3H3zM9 11.5h6" /></svg>}
          </button> : <UserControl size="sm" />}
        </div>
      </header>

      {open && (
        <div className="md:hidden fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="Навигация">
          <button className="absolute inset-0 bg-onyx/35 backdrop-blur-[1px]" onClick={() => setOpen(false)} aria-label="Закрыть меню" />
          <aside className="absolute inset-y-0 left-0 w-[min(86vw,330px)] bg-paper shadow-2xl pt-[env(safe-area-inset-top)] flex flex-col">
            <div className="h-16 px-5 border-b border-rule flex items-center justify-between">
              <div className="flex items-center gap-3"><DiamondMark className="text-ink" /><span className="font-semibold tracking-[0.18em]">ORI</span></div>
              <button onClick={() => setOpen(false)} className="size-11 inline-flex items-center justify-center text-[25px] text-ink-muted" aria-label="Закрыть">×</button>
            </div>
            <nav className="p-3 space-y-1">
              {items.map((item) => {
                const active = isNavigationItemActive(item.href, pathname)
                return <Link key={item.href} to={item.href} aria-current={active ? 'page' : undefined} className={`h-12 px-4 rounded-lg flex items-center gap-3 text-[14px] ${active ? 'bg-bone text-ink font-semibold' : 'text-ink-2'}`}>
                  {item.icon}<span className="truncate">{item.label}</span>
                </Link>
              })}
            </nav>
          </aside>
        </div>
      )}
      <IosInstallPrompt />
    </>
  )
}
